import { useState, useEffect } from 'react';

/**
 * Manages form state for add/edit modals.
 *
 * Previously every modal had its own:
 *   const [formData, setFormData] = useState(defaults);
 *   useEffect(() => { if (initialData) setFormData(initialData); else setFormData(defaults); }, [...]);
 *   const handleChange = (e) => setFormData({...});
 *
 * This hook consolidates that into one reusable place.
 *
 * @param {object}  defaults     – blank form shape (e.g. { category_name: '' })
 * @param {object}  initialData  – record being edited, or null for "add new"
 * @param {boolean} isOpen       – modal open state (resets form on open/close)
 */
export function useFormData(defaults, initialData, isOpen) {
  const [formData, setFormData] = useState(defaults);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData(defaults);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox'
        ? checked
        : type === 'number'
          ? parseFloat(value)
          : value,
    }));
  };

  const resetForm = () => setFormData(defaults);

  return { formData, setFormData, handleChange, resetForm };
}
